import { useCallback, useMemo } from 'react';

import { useCollection } from '@frontend/api';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';
import {
  getAvailableGroupMembers,
  getAvailableGroups,
  getUserGroupCollectionId,
} from 'store/session/selectors';

export const useUserGroupLookup = () => {
  const isMultisiteEnabled = useFeatureFlagValue(
    featureFlagKeys.isMultisiteFunctionalityAvailable,
  );
  const userGroupCollectionId = useAppSelector(getUserGroupCollectionId);
  const availableGroupIds = useAppSelector(getAvailableGroups);
  const availableGroupMembers = useAppSelector(getAvailableGroupMembers);

  const isEnabled = useMemo(() => {
    return isMultisiteEnabled && !!userGroupCollectionId;
  }, [isMultisiteEnabled, userGroupCollectionId]);

  const { data: collection } = useCollection(
    { collectionId: userGroupCollectionId },
    { queryOptions: { enabled: isEnabled } },
  );

  const availableGroups = useMemo(() => {
    const groups = collection?.userGroups ?? [];
    const idSet = new Set(availableGroupIds);
    return groups
      .filter((g) => idSet.has(g.id))
      .toSorted((a, b) => a.businessName.localeCompare(b.businessName));
  }, [collection, availableGroupIds]);

  const userGroupsById = useMemo(() => {
    if (!isEnabled) {
      return {};
    }
    const userGroups = collection?.userGroups ?? [];
    return Object.fromEntries(userGroups.map((group) => [group.id, group]));
  }, [collection, isEnabled]);

  const getUserGroupById = useCallback(
    (groupId: number) => {
      return userGroupsById[groupId];
    },
    [userGroupsById],
  );

  const supplierRolesById = useMemo(() => {
    const rolesByGroupId: Record<number, Record<string, string>> = {};
    availableGroupMembers.forEach((member) => {
      rolesByGroupId[member.group.id] = member.supplierRoles;
    });
    return rolesByGroupId;
  }, [availableGroupMembers]);

  const getSupplierRolesById = useCallback(
    (groupId: number) => {
      return supplierRolesById[groupId] ?? {};
    },
    [supplierRolesById],
  );

  return {
    userGroupsById,
    getUserGroupById,
    availableGroups,
    getSupplierRolesById,
  };
};
